import enUS from 'antd/es/locale/en_US';
import ruRU from 'antd/es/locale/ru_RU';

const isObject = value => value && typeof value === 'object' && !Array.isArray(value);

const deepMerge = (base, override) => {
  const result = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    result[key] = isObject(value) && isObject(base[key]) ? deepMerge(base[key], value) : value;
  });
  return result;
};

const buildUzbek = ({ code, dayjsLocale, words }) => {
  const pickerLang = {
    locale: dayjsLocale,
    placeholder: words.selectDate,
    yearPlaceholder: words.selectYear,
    quarterPlaceholder: words.selectQuarter,
    monthPlaceholder: words.selectMonth,
    weekPlaceholder: words.selectWeek,
    rangePlaceholder: [words.startDate, words.endDate],
    rangeYearPlaceholder: [words.startYear, words.endYear],
    rangeMonthPlaceholder: [words.startMonth, words.endMonth],
    rangeWeekPlaceholder: [words.startWeek, words.endWeek],
    rangeQuarterPlaceholder: [words.startQuarter, words.endQuarter],
    today: words.today,
    now: words.now,
    backToToday: words.backToToday,
    ok: words.ok,
    clear: words.clear,
    month: words.month,
    year: words.year,
    timeSelect: words.selectTime,
    dateSelect: words.selectDate,
    weekSelect: words.selectWeek,
    monthSelect: words.selectMonth,
    yearSelect: words.selectYear,
    decadeSelect: words.selectDecade,
    dateFormat: 'DD.MM.YYYY',
    dateTimeFormat: 'DD.MM.YYYY HH:mm:ss',
    monthBeforeYear: true,
    previousMonth: words.prevMonth,
    nextMonth: words.nextMonth,
    previousYear: words.prevYear,
    nextYear: words.nextYear,
    previousDecade: words.prevDecade,
    nextDecade: words.nextDecade,
    previousCentury: words.prevCentury,
    nextCentury: words.nextCentury,
  };
  const timeLocale = { placeholder: words.selectTime, rangePlaceholder: [words.startTime, words.endTime] };

  return deepMerge(enUS, {
    locale: code,
    Pagination: {
      items_per_page: words.perPage,
      jump_to: words.jumpTo,
      jump_to_confirm: words.ok,
      page: words.page,
      prev_page: words.prevPage,
      next_page: words.nextPage,
      prev_5: words.prev5,
      next_5: words.next5,
      prev_3: words.prev3,
      next_3: words.next3,
      page_size: words.pageSize,
    },
    DatePicker: { lang: pickerLang, timePickerLocale: timeLocale },
    Calendar: { lang: pickerLang, timePickerLocale: timeLocale },
    TimePicker: timeLocale,
    global: { placeholder: words.select },
    Table: {
      filterTitle: words.filter,
      filterConfirm: words.ok,
      filterReset: words.reset,
      filterEmptyText: words.noFilters,
      filterCheckall: words.selectAll,
      filterSearchPlaceholder: words.search,
      emptyText: words.noData,
      selectAll: words.selectAll,
      selectInvert: words.invert,
      selectNone: words.clear,
      selectionAll: words.selectAll,
      sortTitle: words.sort,
      expand: words.expand,
      collapse: words.collapse,
      triggerDesc: words.sortDesc,
      triggerAsc: words.sortAsc,
      cancelSort: words.cancelSort,
    },
    Modal: { okText: words.ok, cancelText: words.cancel, justOkText: words.ok },
    Popconfirm: { okText: words.ok, cancelText: words.cancel },
    Transfer: { searchPlaceholder: words.search, itemUnit: words.item, itemsUnit: words.item },
    Empty: { description: words.noData },
    Text: { edit: words.edit, copy: words.copy, copied: words.copied, expand: words.expand },
    PageHeader: { back: words.back },
    Form: { optional: words.optional },
  });
};

const uzLatn = buildUzbek({
  code: 'uz-latn',
  dayjsLocale: 'uz-latn',
  words: {
    selectDate: 'Sanani tanlang',
    selectYear: 'Yilni tanlang',
    selectQuarter: 'Chorakni tanlang',
    selectMonth: 'Oyni tanlang',
    selectWeek: 'Haftani tanlang',
    selectDecade: 'O‘n yillikni tanlang',
    selectTime: 'Vaqtni tanlang',
    startDate: 'Boshlanish',
    endDate: 'Tugash',
    startYear: 'Boshlanish yili',
    endYear: 'Tugash yili',
    startMonth: 'Boshlanish oyi',
    endMonth: 'Tugash oyi',
    startWeek: 'Boshlanish haftasi',
    endWeek: 'Tugash haftasi',
    startQuarter: 'Boshlanish choragi',
    endQuarter: 'Tugash choragi',
    startTime: 'Boshlanish vaqti',
    endTime: 'Tugash vaqti',
    today: 'Bugun',
    now: 'Hozir',
    backToToday: 'Bugunga qaytish',
    ok: 'OK',
    clear: 'Tozalash',
    month: 'Oy',
    year: 'Yil',
    prevMonth: 'Oldingi oy',
    nextMonth: 'Keyingi oy',
    prevYear: 'Oldingi yil',
    nextYear: 'Keyingi yil',
    prevDecade: 'Oldingi o‘n yillik',
    nextDecade: 'Keyingi o‘n yillik',
    prevCentury: 'Oldingi asr',
    nextCentury: 'Keyingi asr',
    perPage: '/ sahifa',
    jumpTo: 'O‘tish',
    page: 'Sahifa',
    prevPage: 'Oldingi sahifa',
    nextPage: 'Keyingi sahifa',
    prev5: 'Oldingi 5 sahifa',
    next5: 'Keyingi 5 sahifa',
    prev3: 'Oldingi 3 sahifa',
    next3: 'Keyingi 3 sahifa',
    pageSize: 'Sahifa hajmi',
    select: 'Tanlang',
    filter: 'Filtr',
    reset: 'Tozalash',
    noFilters: 'Filtrlar yo‘q',
    selectAll: 'Barchasini tanlash',
    invert: 'Teskari tanlash',
    search: 'Qidirish',
    noData: 'Ma’lumot yo‘q',
    sort: 'Saralash',
    expand: 'Yoyish',
    collapse: 'Yig‘ish',
    sortAsc: 'O‘sish bo‘yicha',
    sortDesc: 'Kamayish bo‘yicha',
    cancelSort: 'Saralashni bekor qilish',
    cancel: 'Bekor qilish',
    item: 'ta',
    edit: 'Tahrirlash',
    copy: 'Nusxa olish',
    copied: 'Nusxa olindi',
    back: 'Orqaga',
    optional: '(ixtiyoriy)',
  },
});

const uzCyrl = buildUzbek({
  code: 'uz',
  dayjsLocale: 'uz',
  words: {
    selectDate: 'Санани танланг',
    selectYear: 'Йилни танланг',
    selectQuarter: 'Чоракни танланг',
    selectMonth: 'Ойни танланг',
    selectWeek: 'Ҳафтани танланг',
    selectDecade: 'Ўн йилликни танланг',
    selectTime: 'Вақтни танланг',
    startDate: 'Бошланиш',
    endDate: 'Тугаш',
    startYear: 'Бошланиш йили',
    endYear: 'Тугаш йили',
    startMonth: 'Бошланиш ойи',
    endMonth: 'Тугаш ойи',
    startWeek: 'Бошланиш ҳафтаси',
    endWeek: 'Тугаш ҳафтаси',
    startQuarter: 'Бошланиш чораги',
    endQuarter: 'Тугаш чораги',
    startTime: 'Бошланиш вақти',
    endTime: 'Тугаш вақти',
    today: 'Бугун',
    now: 'Ҳозир',
    backToToday: 'Бугунга қайтиш',
    ok: 'OK',
    clear: 'Тозалаш',
    month: 'Ой',
    year: 'Йил',
    prevMonth: 'Олдинги ой',
    nextMonth: 'Кейинги ой',
    prevYear: 'Олдинги йил',
    nextYear: 'Кейинги йил',
    prevDecade: 'Олдинги ўн йиллик',
    nextDecade: 'Кейинги ўн йиллик',
    prevCentury: 'Олдинги аср',
    nextCentury: 'Кейинги аср',
    perPage: '/ саҳифа',
    jumpTo: 'Ўтиш',
    page: 'Саҳифа',
    prevPage: 'Олдинги саҳифа',
    nextPage: 'Кейинги саҳифа',
    prev5: 'Олдинги 5 саҳифа',
    next5: 'Кейинги 5 саҳифа',
    prev3: 'Олдинги 3 саҳифа',
    next3: 'Кейинги 3 саҳифа',
    pageSize: 'Саҳифа ҳажми',
    select: 'Танланг',
    filter: 'Фильтр',
    reset: 'Тозалаш',
    noFilters: 'Фильтрлар йўқ',
    selectAll: 'Барчасини танлаш',
    invert: 'Тескари танлаш',
    search: 'Қидириш',
    noData: 'Маълумот йўқ',
    sort: 'Саралаш',
    expand: 'Ёйиш',
    collapse: 'Йиғиш',
    sortAsc: 'Ўсиш бўйича',
    sortDesc: 'Камайиш бўйича',
    cancelSort: 'Саралашни бекор қилиш',
    cancel: 'Бекор қилиш',
    item: 'та',
    edit: 'Таҳрирлаш',
    copy: 'Нусха олиш',
    copied: 'Нусха олинди',
    back: 'Орқага',
    optional: '(ихтиёрий)',
  },
});

const ru = deepMerge(ruRU, {
  DatePicker: { lang: { dateFormat: 'DD.MM.YYYY' } },
});

export const antdLocales = {
  uz: uzLatn,
  'uz-Cyrl': uzCyrl,
  ru,
  en: enUS,
};
